/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import APM from '../APM';
import getMonitor from '../monitors/PagePerformanceMonitor';

export type UseStopPageMonitorResult = () => void

/**
 * Provides a convenience hook which can be used to explicitly complete page
 * performance monitoring imperatively.
 *
 * This hook is intended for use-case where a child component needs to defer
 * invoking `PagePerformanceMonitor.stop` until a specific state has been
 * obtained.
 *
 * ```typescript
 *   import useStopPageMonitor from '@ion/telemetry/src/apm/hooks/useStopPageMonitor';
 *   ...
 *
 *   const SomeComponent = () => {
 *     const pageMonitorComplete = useStopPageMonitor();
 *     useEffect(() => {
 *       if (someState && someOtherState) {
 *         pageMonitorComplete();
 *       }
 *     }, [someState, someOtherState])
 *   };
 * ```
 *
 *
 * @returns A callback function which, when invoked, will explicitly complete
 *          the page performance monitoring process
 */
export const useStopPageMonitor = (): UseStopPageMonitorResult => {
  const onComplete = useCallback(() => {
    if (APM.isEnabled) {
      const monitor = getMonitor();
      if (monitor?.isRunning()) {
        monitor.stop();
      }
    }
  }, []);
  return onComplete;
};
export default useStopPageMonitor;
